import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import LineItemsEditor from "@/components/LineItemsEditor";

export default function QuotationForm() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inquiry_id = params.get("inquiry_id");
  const dc_id = params.get("dc_id");
  const [inquiry, setInquiry] = useState(null);
  const [dc, setDc] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!inquiry_id || !!dc_id);

  const [form, setForm] = useState({
    inquiry_id: inquiry_id || "",
    delivery_challan_id: dc_id || "",
    delivery_challan_number: "",
    delivery_challan_date: "",
    customer_id: "",
    plant_id: "",
    quotation_type: dc_id ? "L" : "M",
    quotation_date: new Date().toISOString().slice(0, 10),
    validity_days: 30,
    terms: "50% advance, 50% before dispatch. Delivery ex-works. Prices exclusive of GST.",
    remarks: "",
    line_items: [],
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    if (inquiry_id) {
      api.get(`/inquiries/${inquiry_id}`).then((r) => {
        const d = r.data;
        setInquiry(d);
        setForm((f) => ({
          ...f,
          inquiry_id,
          customer_id: d.customer_id,
          plant_id: d.plant_id,
          line_items: (d.line_items || []).map((li) => ({
            description: li.description,
            cylinder_type: li.cylinder_type,
            manufacturing_type: li.manufacturing_type || "New",
            customer_material_code: li.customer_material_code || "",
            application: li.application,
            bore_mm: li.bore_mm,
            rod_diameter_mm: li.rod_diameter_mm,
            stroke_mm: li.stroke_mm,
            quantity: li.quantity,
            operating_pressure_bar: li.operating_pressure_bar,
            remarks: li.remarks,
            unit_price: 0,
          })),
        }));
      }).finally(() => setLoading(false));
    } else if (dc_id) {
      api.get(`/receiving/${dc_id}`).then((r) => {
        const d = r.data;
        setDc(d);
        // Default one blank recon line pre-filled with quantity = num_cylinders_received
        setForm((f) => ({
          ...f,
          delivery_challan_id: dc_id,
          delivery_challan_number: d.dc_number,
          delivery_challan_date: d.dc_date,
          customer_id: d.customer_id,
          plant_id: d.plant_id || "",
          quotation_type: "L",
          line_items: [{
            description: `Reconditioning against DC ${d.dc_number}`,
            cylinder_type: "Hydraulic",
            manufacturing_type: "Reconditioning",
            customer_material_code: "",
            application: "",
            bore_mm: 0, rod_diameter_mm: 0, stroke_mm: 0,
            quantity: d.num_cylinders_received || 1,
            operating_pressure_bar: 0,
            remarks: "",
            unit_price: 0,
          }],
        }));
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiry_id, dc_id]);

  useEffect(() => {
    if (!form.customer_id || inquiry) return;
    api.get(`/plants?customer_id=${form.customer_id}`).then((r) => setPlants(r.data));
  }, [form.customer_id, inquiry]);

  async function submit(e) {
    e.preventDefault();
    // Either inquiry_id OR (delivery_challan_number + customer_id + plant_id)
    const hasInq = !!form.inquiry_id;
    const hasDc = !!(form.delivery_challan_number || form.delivery_challan_id);
    if (!hasInq && !hasDc) {
      toast.error("Provide either an Inquiry link OR a Delivery Challan number");
      return;
    }
    if (hasDc && !hasInq && (!form.customer_id || !form.plant_id)) {
      toast.error("Customer and Plant are required for DC-based quotations");
      return;
    }
    if (form.line_items.length === 0) { toast.error("At least one line item"); return; }
    if (form.line_items.some((li) => !li.unit_price || li.unit_price <= 0)) {
      toast.error("Every line must have a unit price > 0"); return;
    }
    setSaving(true);
    try {
      // Strip empty strings from optional fields
      const body = { ...form };
      if (!body.inquiry_id) delete body.inquiry_id;
      if (!body.delivery_challan_id) delete body.delivery_challan_id;
      if (!body.delivery_challan_number) delete body.delivery_challan_number;
      if (!body.delivery_challan_date) delete body.delivery_challan_date;
      const { data } = await api.post("/quotations", body);
      toast.success(`Quotation ${data.quotation_number} created (Draft)`);
      nav(`/quotations/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;

  return (
    <div data-testid="quotation-form-page" className="max-w-5xl space-y-5">
      <div>
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>New Quotation</h1>
        {inquiry && (
          <p className="text-sm text-slate-500 mt-1">
            From inquiry <span className="font-mono font-semibold text-slate-800">{inquiry.inquiry_number}</span> · {inquiry.customer_name} · {inquiry.plant_name}
          </p>
        )}
        {dc && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
            <strong>Reconditioning · Cylinder Received First</strong> — quotation against Delivery Challan <span className="font-mono font-semibold">{dc.dc_number}</span> · {dc.customer_name} · {dc.dc_date}
          </p>
        )}
      </div>

      <form onSubmit={submit} className="space-y-5">
        <Card className="border-slate-200 p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Quotation Type *</Label>
            <select
              data-testid="qtn-type"
              value={form.quotation_type}
              onChange={(e) => setForm({ ...form, quotation_type: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="M">Manufacturing (M)</option>
              <option value="L">Reconditioning / Repair (L)</option>
              <option value="B">Budgetary (B)</option>
            </select>
            <p className="mt-1 text-[10px] text-slate-500">
              Series: {form.quotation_type === "M" ? "NNNM/FY (e.g. 120M/26-27)"
                : form.quotation_type === "L" ? "NNNL/FY (e.g. 119L/26-27)"
                : "NNNB/FY (e.g. 003B/26-27)"} · Revisions append -A / -B / -C …
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Quotation Date *</Label>
            <Input type="date" data-testid="qtn-date" value={form.quotation_date}
                   onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Validity (days)</Label>
            <Input type="number" min="1" data-testid="qtn-validity" value={form.validity_days}
                   onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) })} className="mt-1 font-mono" />
          </div>
          {/* Delivery Challan reference — appears when no parent inquiry (Reconditioning · Cylinder Received First) OR user opts in */}
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-slate-100 pt-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Delivery Challan # {!form.inquiry_id && <span className="text-red-600">*</span>}
              </Label>
              <Input data-testid="qtn-dc-number" className="mt-1 font-mono"
                     value={form.delivery_challan_number}
                     onChange={(e) => setForm({ ...form, delivery_challan_number: e.target.value })}
                     placeholder="Manually enter (customer-provided)" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">DC Date</Label>
              <Input type="date" data-testid="qtn-dc-date" className="mt-1 font-mono"
                     value={form.delivery_challan_date}
                     onChange={(e) => setForm({ ...form, delivery_challan_date: e.target.value })} />
            </div>
            {!form.inquiry_id && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Customer *</Label>
                  <select className="mt-1 w-full h-9 border border-slate-300 rounded px-2 text-sm"
                          data-testid="qtn-customer"
                          value={form.customer_id}
                          onChange={(e) => setForm({ ...form, customer_id: e.target.value, plant_id: "" })}>
                    <option value="">Select customer</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Plant *</Label>
                  <select className="mt-1 w-full h-9 border border-slate-300 rounded px-2 text-sm"
                          data-testid="qtn-plant"
                          value={form.plant_id}
                          onChange={(e) => setForm({ ...form, plant_id: e.target.value })}>
                    <option value="">Select plant</option>
                    {plants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
        </Card>

        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Cylinder Line Items</div>
            <div className="text-xs text-slate-500">Set unit price per line — total value auto-computes.</div>
          </div>
          <div className="p-5">
            <LineItemsEditor value={form.line_items} customerId={inquiry?.customer_id || form.customer_id} onChange={(v) => setForm({ ...form, line_items: v })} showPrice />
          </div>
        </Card>

        <Card className="border-slate-200 p-5 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Terms & Conditions</Label>
            <Textarea data-testid="qtn-terms" value={form.terms} rows={3}
                      onChange={(e) => setForm({ ...form, terms: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Remarks</Label>
            <Textarea value={form.remarks} rows={2}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1" />
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>Cancel</Button>
          <Button type="submit" data-testid="qtn-submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Quotation"}
          </Button>
        </div>
      </form>
    </div>
  );
}
